/**
 * CSRF Token Refresh Utility
 * Provides unified function to refresh CSRF token from server
 */

/**
 * Refresh CSRF token from server
 * Updates meta tag and optionally form input field
 * 
 * @param {HTMLElement|null} form - Optional form element to update input[name="_token"]
 * @returns {Promise<{success: boolean, token: string|null}>} Object with success status and token value
 */
export function refreshCsrfToken(form = null) {
    return fetch(window.location.href, {
        method: 'GET',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin'
    })
    .then(response => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
    })
    .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newToken = doc.querySelector('meta[name="csrf-token"]');
        
        if (!newToken) {
            console.error('CSRF token meta tag not found in response');
            return { success: false, token: null };
        }
        
        const tokenValue = newToken.getAttribute('content');
        
        if (!tokenValue) {
            console.error('CSRF token value is empty');
            return { success: false, token: null };
        }
        
        // Update meta tag
        const metaToken = document.querySelector('meta[name="csrf-token"]');
        if (metaToken) {
            metaToken.setAttribute('content', tokenValue);
        }
        
        // Update form input if form is provided
        if (form) {
            const csrfInput = form.querySelector('input[name="_token"]');
            if (csrfInput) {
                csrfInput.value = tokenValue;
            }
        }
        
        return { success: true, token: tokenValue };
    })
    .catch(error => {
        console.error('Error refreshing CSRF token:', error);
        return { success: false, token: null };
    });
}

/**
 * Refresh CSRF token and return only the token value (for backward compatibility)
 * 
 * @param {HTMLElement|null} form - Optional form element to update input[name="_token"]
 * @returns {Promise<string|null>} Token value or null if failed
 */
export function refreshCsrfTokenValue(form = null) {
    return refreshCsrfToken(form).then(result => result.token);
}

/**
 * Refresh CSRF token and return only success status (for backward compatibility)
 * 
 * @param {HTMLElement|null} form - Optional form element to update input[name="_token"]
 * @returns {Promise<boolean>} True if token was refreshed successfully
 */
export function refreshCsrfTokenSuccess(form = null) {
    return refreshCsrfToken(form).then(result => result.success);
}

// Default export
export default refreshCsrfToken;
